import React from 'react';
import autoBind from 'react-autobind';
import { Layout, PageHeader, Select, Row, Col, DatePicker, Switch, Button, Input, Space, message } from 'antd';
import { DownloadOutlined, SearchOutlined } from '@ant-design/icons';
import moment from 'moment';
//
import CommonLoadingView from '../../commonComponents/CommonLoadingView';
import CustomComponent from '../../../components/CustomComponent';
import config from '../../../config/config';
import Utils from '../../../components/Utils';
import Globals from '../../../config/Globals';
import CommonApplicationsTable from '../../commonComponents/CommonApplicationsTable';

export default class AdminApplicationReportView extends React.Component {
  constructor(props) {
    super(props);
    autoBind(this);

    this.state = {
      ...this._getInitialState(),
      applications: [],
      isLoading: false,
      isDownloading: false,
    };
  }

  _getInitialState() {
    let pState = {};
    try {
      const param = this.props.app.idm.urlmanager.getParam('filters');
      if (param) {
        pState = JSON.parse(Buffer.from(param, 'base64').toString('ascii'));
      }
    } catch (e) {
      console.warn('Failed to parse filters param', e);
    }

    const convertDateRange = (dateRange) => {
      if (Array.isArray(dateRange) && dateRange.length === 2) {
        return [moment(dateRange[0]), moment(dateRange[1])];
      }
      return [];
    };

    return {
      createdDateRange: convertDateRange(pState.createdDateRange),
      submittedDateRange: convertDateRange(pState.submittedDateRange),
      reviewedDateRange: convertDateRange(pState.reviewedDateRange),
      certificationID: pState.certificationID || this.props.app.sharedCache().getTenantConfig().certifications[0].id,
      applicationStatus: pState.applicationStatus || Globals.USER_APPLICATION_FILTERS[0]?.key || null,
      applicationType: pState.applicationType || 'NEW',
      userStatus: pState.userStatus || null,
      ignoreCompletedCertifications: pState.ignoreCompletedCertifications ?? true,
      searchTerm: pState.searchTerm || '',
    };
  }

  componentDidMount() {
    const {
      searchTerm,
      createdDateRange,
      submittedDateRange,
      reviewedDateRange,
      userStatus,
      applicationStatus,
      certificationID,
    } = this.state;
    if (
      searchTerm ||
      createdDateRange.length ||
      submittedDateRange.length ||
      reviewedDateRange.length ||
      userStatus ||
      applicationStatus ||
      certificationID
    ) {
      this.handleSearch();
    }
  }

  componentDidUpdate(prevProps, prevState) {
    const keysToTrack = [
      'certificationID',
      'applicationStatus',
      'userStatus',
      'createdDateRange',
      'submittedDateRange',
      'reviewedDateRange',
      'ignoreCompletedCertifications',
      'searchTerm',
    ];

    const hasRelevantChange = keysToTrack.some(
      (key) => JSON.stringify(this.state[key]) !== JSON.stringify(prevState[key])
    );
    if (hasRelevantChange) this._setSearchQueryParams();
  }

  _setSearchQueryParams() {
    const cleanState = { ...this.state };
    ['isLoading', 'isDownloading', 'users'].forEach((key) => delete cleanState[key]);

    const serializeDateRange = (range) => {
      if (Array.isArray(range) && range.length === 2) {
        return [range[0].toISOString(), range[1].toISOString()];
      }
      return [];
    };

    cleanState.createdDateRange = serializeDateRange(cleanState.createdDateRange);
    cleanState.submittedDateRange = serializeDateRange(cleanState.submittedDateRange);
    cleanState.reviewedDateRange = serializeDateRange(cleanState.reviewedDateRange);

    this.props.app.urlManager.updateQueryStringParam(
      'filters',
      Buffer.from(JSON.stringify(cleanState)).toString('base64')
    );
  }

  async handleExport() {
    const {
      createdDateRange,
      submittedDateRange,
      reviewedDateRange,
      ignoreCompletedCertifications,
      userStatus,
      applicationStatus,
      certificationID,
      applicationType,
    } = this.state;

    const createdDateRangeValue = createdDateRange || [];
    const submittedDateRangeValue = submittedDateRange || [];
    const reviewedDateRangeValue = reviewedDateRange || [];

    const payload = {
      certificationID: certificationID,
      type: applicationType || null,
      state: applicationStatus,
      submittedFrom: submittedDateRangeValue[0] ? submittedDateRangeValue[0].startOf('day').valueOf() : null,
      submittedTo: submittedDateRangeValue[1] ? submittedDateRangeValue[1].endOf('day').valueOf() : null,
      reviewedFrom: reviewedDateRangeValue[0] ? reviewedDateRangeValue[0].startOf('day').valueOf() : null,
      reviewedTo: reviewedDateRangeValue[1] ? reviewedDateRangeValue[1].endOf('day').valueOf() : null,
      createdFrom: createdDateRangeValue[0] ? createdDateRangeValue[0].startOf('day').valueOf() : null,
      createdTo: createdDateRangeValue[1] ? createdDateRangeValue[1].endOf('day').valueOf() : null,
      userCourseStatus: userStatus,
      ignoreCompletedCertifications,
    };

    this.setState({ isDownloading: true });
    try {
      const resp = await this.props.app.api.application.getApplicationsExport(payload);
      if (resp.statusCode === 200) {
        Utils.downloadBlob(resp.body, 'ApplicationsReport', 'xlsx');
      } else {
        this.props.app.alertController.showAPIErrorAlert(null, resp);
      }
    } catch (error) {
      message.error('An error occurred while exporting the report.');
      console.error(error);
    } finally {
      this.setState({ isDownloading: false });
    }
  }

  async handleSearch() {
    const {
      createdDateRange,
      submittedDateRange,
      reviewedDateRange,
      ignoreCompletedCertifications,
      userStatus,
      applicationStatus,
      certificationID,
      applicationType,
    } = this.state;

    const createdDateRangeValue = createdDateRange || [];
    const submittedDateRangeValue = submittedDateRange || [];
    const reviewedDateRangeValue = reviewedDateRange || [];

    const payload = {
      certificationID: certificationID,
      type: applicationType || null,
      state: applicationStatus,
      submittedFrom: submittedDateRangeValue[0] ? submittedDateRangeValue[0].startOf('day').valueOf() : null,
      submittedTo: submittedDateRangeValue[1] ? submittedDateRangeValue[1].endOf('day').valueOf() : null,
      reviewedFrom: reviewedDateRangeValue[0] ? reviewedDateRangeValue[0].startOf('day').valueOf() : null,
      reviewedTo: reviewedDateRangeValue[1] ? reviewedDateRangeValue[1].endOf('day').valueOf() : null,
      createdFrom: createdDateRangeValue[0] ? createdDateRangeValue[0].startOf('day').valueOf() : null,
      createdTo: createdDateRangeValue[1] ? createdDateRangeValue[1].endOf('day').valueOf() : null,
      userCourseStatus: userStatus,
      ignoreCompletedCertifications,
    };

    this.setState({ isLoading: true });
    try {
      const resp = await this.props.app.api.application.getApplicationsReport(payload);
      this.setState({ applications: resp.body.applications || [] });
    } catch (error) {
      console.error('Search error:', error);
      message.error('An error occurred while searching.');
    } finally {
      this.setState({ isLoading: false });
    }
  }

  render() {
    const availableApplicationStatus = Globals.USER_APPLICATION_FILTERS;
    const availableUserStatus = Globals.USER_APPLICATION_STATUS;

    return (
      <Layout.Content className="pageContent">
        <CommonLoadingView isLoading={this.state.isLoading} />
        <PageHeader className="pageHeader" title="Applications Report" />
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
          <Button type="primary" onClick={this.handleExport} disabled={this.state.isDownloading}>
            {this.state.isDownloading ? 'Exporting...' : 'Export to XLSX'}
          </Button>
        </div>
        <Layout.Content>
          <div className="usersFilters">
            <Row gutter={16}>
              <Col span={6}>
                <strong>Certification:</strong>
                <Select
                  value={this.state.certificationID}
                  onChange={(certificationID) => this.setState({ certificationID })}
                  style={{ width: '100%' }}
                >
                  {this.props.app
                    .sharedCache()
                    .getTenantConfig()
                    .certifications.map((cert) => (
                      <Select.Option key={cert.id} value={cert.id}>
                        {cert.description}
                      </Select.Option>
                    ))}
                </Select>
              </Col>

              <Col span={6}>
                <strong>Type:</strong>
                <Select
                  value={this.state.applicationType}
                  onChange={(applicationType) => this.setState({ applicationType })}
                  allowClear
                  style={{ width: '100%' }}
                >
                  <Select.Option value="NEW">NEW</Select.Option>
                  <Select.Option value="RENEW">RENEW</Select.Option>
                </Select>
              </Col>

              <Col span={6}>
                <strong>Application Status:</strong>
                <Select
                  value={this.state.applicationStatus}
                  onChange={(applicationStatus) => this.setState({ applicationStatus })}
                  style={{ width: '100%' }}
                >
                  {availableApplicationStatus.map((option) => (
                    <Select.Option key={option.key} value={option.key}>
                      {option.label}
                    </Select.Option>
                  ))}
                </Select>
              </Col>
              <Col span={6}>
                <strong>User Status:</strong>
                <Select
                  value={this.state.userStatus}
                  onChange={(userStatus) => this.setState({ userStatus })}
                  allowClear
                  style={{ width: '100%' }}
                >
                  {availableUserStatus.map((option) => (
                    <Select.Option key={option.key} value={option.key}>
                      {option.label}
                    </Select.Option>
                  ))}
                </Select>
              </Col>
            </Row>

            <Row gutter={16} style={{ marginTop: 16 }}>
              <Col span={8}>
                <strong>Application Creation Date:</strong>
                <DatePicker.RangePicker
                  value={this.state.createdDateRange}
                  onChange={(createdDateRange) => this.setState({ createdDateRange })}
                  style={{ width: '100%' }}
                />
              </Col>
              <Col span={8}>
                <strong>Application Submission Date:</strong>
                <DatePicker.RangePicker
                  value={this.state.submittedDateRange}
                  onChange={(submittedDateRange) => this.setState({ submittedDateRange })}
                  style={{ width: '100%' }}
                />
              </Col>
              <Col span={8}>
                <strong>Application Review Date:</strong>
                <DatePicker.RangePicker
                  value={this.state.reviewedDateRange}
                  onChange={(reviewedDateRange) => this.setState({ reviewedDateRange })}
                  style={{ width: '100%' }}
                />
              </Col>
            </Row>

            <Row style={{ marginTop: 16 }}>
              <Col span={12}>
                <strong style={{ marginRight: 8 }}>Ignore Completed Certifications:</strong>
                <Space size={8}>
                  <Switch
                    checked={this.state.ignoreCompletedCertifications}
                    onChange={(ignoreCompletedCertifications) => this.setState({ ignoreCompletedCertifications })}
                  />
                  <Button type="primary" icon={<SearchOutlined />} onClick={this.handleSearch}>
                    Search
                  </Button>
                </Space>
              </Col>
            </Row>
            <br />
            <CommonApplicationsTable data={this.state.applications} app={this.props.app} />
          </div>
        </Layout.Content>
      </Layout.Content>
    );
  }
}
